import * as THREE from 'three';
import { gsap } from "gsap";
import YinkaColor from './YinkaColors.js';
import { createNoise2D } from 'simplex-noise';
import {shuffle} from './Utils.js';


export default class HexFloor {

  constructor(scene,grassTex){
    this.greenTiles = [];
    this.allTiles = [];
    this.yinkaColors = new YinkaColor();
    this.noise2D = createNoise2D();
    this.scene = scene;
    this.grassTex = grassTex;
    this.hexSize = 1.65;
    this.floorData = [];

    let purples = [0xFE76E2,0xEA6DEA,0xDB67F0,0xCB60F7,0xB858FE];
    let pinks = [0xF9B2CF,0xFB89CB,0xFC67C8,0xFD4DC6,0xFF1AC1];
    let oranges = [0xF1AB33,0xEB992E,0xE78B2A,0xE17727,0xE45F31];

    //f8a726
    this.gradients = {
      "kalimba":{center:0xF8C43A,tiles:oranges},
      "percussion":{center:0xFFE744,tiles:pinks},
      "bass":{center:0xF8C43A,tiles:purples},
    };

  }

  generateFloorData(imgMap=null,benchTiles=[]){

    this.floorData = [];

    let imgdata;

    if(imgMap!=null){
      //console.log("hex load with imgMap");
      let canvas =document.createElement('canvas');
      canvas.width = 20;
      canvas.height = 20;
      let context = canvas.getContext("2d");
      context.drawImage( imgMap.image, 0, 0 );
      imgdata = context.getImageData( 0, 0, canvas.width, canvas.height ).data;
    }

    let greenTiles = [];
    // let benchTiles = [48,138,344,249];
    let index = 0;

    for(let i = -10; i < 10; i++){
      for(let j = -10; j < 10; j++){
        let position = this.hexGridTileToPosition(i,j,this.hexSize);
        let len = position.length();
        let usable = this.useTile(len,imgMap);
        let noise = 1*(this.noise2D(i*.15,j*.15) + 1);// * .5;
        noise = Math.pow(noise,.05*len) * 2;

        if(imgMap != null)
          noise = 12 * (imgdata[ (( (i+10)*4) * 20 + ((j+10)*4)) ] / 255);

        if(imgMap != null && len > 4*this.hexSize && noise <= 0){
            usable = false;
        }

        if(len < 4*this.hexSize || noise < .15){
           noise = .15;
        }else if(len < 10*this.hexSize){
           noise *= .5;
        }

        if(!usable){
          noise = 0;
        }

        let data = {"position":position,"i":i,"j":j,"noise":noise,"len":len,"tree":0,"usable":usable};
        this.floorData.push( data );

        if(len > 8 * this.hexSize && !benchTiles.includes(index) && usable){
          greenTiles.push(this.floorData.length-1);
        }
        index++;
      }
    }

    shuffle(greenTiles);

    let maxTrees = Math.min(45,greenTiles.length);
    for(let i = 0; i < maxTrees; i++){
      let index = greenTiles[i];
      this.floorData[index]["tree"] = 1;
    }

    if(benchTiles.length > 0){
      benchTiles.forEach((item, i) => {
        if(this.floorData.length > item){
          this.floorData[item]["bench"] = 1;
          this.floorData[item]["holdForBench"] = true;
        }

      });
    }

  }

  updateTileHeights(floorData){
      floorData.forEach((item, i) => {
        this.floorData[i].noise = item.noise;
        this.allTiles[i].scale.y = item.noise;
      });

  }

  useTile(len,imgMap){
    let used = false;
    if(len > 16*this.hexSize)
      return false;

    if( len < 3*this.hexSize )
      return false;

    if(imgMap != null)
      return true;
    else if( len > 8 * this.hexSize || len < 4*this.hexSize )
      return true;

    return used;
  }

  makeInstancedFloor(){
    let count = 20*20;

    if(!this.instanceMesh){
      let geo = new THREE.CylinderGeometry( this.hexSize, this.hexSize, 1, 6, 1, false );
      let mat = new THREE.MeshToonMaterial( {color: new THREE.Color(0xececec)} ));

      this.instanceMesh = new THREE.InstancedMesh(geo, mat, count);
      this.instanceMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // will be updated every frame
      this.instanceMesh.castShadow = true;
      this.instanceMesh.receiveShadow = true;
      this.instanceMesh.name = "hex";
      this.scene.add(this.instanceMesh);
    }


    for ( let i = 0; i < count; i++ ) {
      let color = new THREE.Color( 0xececec );
      this.instanceMesh.setColorAt( i, color );
    }

    let dummy = new THREE.Object3D();
    this.floorData.forEach((item, i) => {
      let _position = item.position;
      let _scale = {x:1,y:item.noise,z:1};
      if(item.noise <= 0){
        _scale = {x:0,y:0,z:0};
      }
      dummy.position.set( item.position.x,0,item.position.y );
      dummy.scale.set( _scale.x,_scale.y,_scale.z );
      dummy.updateMatrix();
      this.instanceMesh.setMatrixAt( i, dummy.matrix );

      let color = this.getTileGrayColor(item.len);
      this.instanceMesh.setColorAt( i, color );
    });

    this.instanceMesh.instanceMatrix.needsUpdate = true;
    this.instanceMesh.instanceColor.needsUpdate = true;

  }

  changeTileColor(i,color){
    if(color && this.instanceMesh){
      this.instanceMesh.setColorAt( i, color );
      this.instanceMesh.instanceColor.needsUpdate = true;
    }
  }

  tweenTileColor(i,currentColor,final,duration){
    let initialColor = new THREE.Color().copy(currentColor);
    gsap.to(initialColor, {
      r: final.r,
      g: final.g,
      b: final.b,
      ease : 'sine.inOut',
      duration : duration,
      overwrite:true,
      onUpdate: function(){
        this.instanceMesh.setColorAt( i,initialColor);
        this.instanceMesh.instanceColor.needsUpdate = true;
      }.bind(this),
      onComplete: function(){}
    });

  }

  blinkTile(i,realColor,highlightColor,prevColor){
    let tl = gsap.timeline({repeat: -1});
    tl.to(realColor,{
      r: highlightColor.r,
      g: highlightColor.g,
      b: highlightColor.b,
      ease : 'sine.inOut',
      duration : 1,
      onUpdate: function(){
        this.instanceMesh.setColorAt( i,realColor);
        this.instanceMesh.instanceColor.needsUpdate = true;
      }.bind(this)
    });
    tl.to(realColor,{
      r: prevColor.r,
      g: prevColor.g,
      b: prevColor.b,
      ease : 'sine.inOut',
      duration : 1,
      onUpdate: function(){
        this.instanceMesh.setColorAt( i,realColor);
        this.instanceMesh.instanceColor.needsUpdate = true;
      }.bind(this)
    });
    return tl;
  }

  highlightTileOnTone(i,realColor,highlightColor,prevColor){
    let tl = gsap.timeline({repeat: 1});
    tl.to(realColor,{
      r: highlightColor.r,
      g: highlightColor.g,
      b: highlightColor.b,
      ease : 'sine.inOut',
      duration :.25,
      onUpdate: function(){
        this.instanceMesh.setColorAt( i,realColor);
        this.instanceMesh.instanceColor.needsUpdate = true;
      }.bind(this)
    });
    tl.to(realColor,{
      r: prevColor.r,
      g: prevColor.g,
      b: prevColor.b,
      ease : 'sine.inOut',
      duration : 1,
      onUpdate: function(){
        this.instanceMesh.setColorAt( i,realColor);
        this.instanceMesh.instanceColor.needsUpdate = true;
      }.bind(this)
    });
    return tl;
  }

  async makeFloor(instrument){

    if(this.floorData.length == 0 )
      this.generateFloorData();

    let size = this.hexSize;
    let gradient = this.gradients[instrument].tiles;//[0xF1AB33,0xEB992E,0xE78B2A,0xE17727,0xE45F31];
    let centerColor = this.gradients[instrument].center;
    // let gradient = [0xFCCE39,0xEB8735,0xDF7026,0xD44F2C,0xBB3E26];
    this.centerTiles = [];

    this.floorData.forEach((item, i) => {
      let position = item.position;
      let len = item.len;

      let color = this.getTileColor(len,instrument);

      let mesh = {};//this.makeHex(item.noise, item.position);
      mesh.position = new THREE.Vector3(item.position.x,0,item.position.y);
      mesh.material = new THREE.MeshToonMaterial( {color: new THREE.Color(0xececec) } );
      mesh.scale = new THREE.Vector3(1,item.noise,1);
      mesh.finalColor = color;
      mesh.len = len;
      mesh.tileType = "default";
      mesh.gridPos = [item.i,item.j];
      mesh.onoise = item.noise;
      mesh.tileIndex = i;
      mesh.plants = 0;
      mesh.usable = item.usable;
      mesh.visible = item.usable;


      if(item.hasOwnProperty("bench"))
        mesh.bench = item.bench;

      if(len < 5*size ){
        color = new THREE.Color(centerColor);
        mesh.tileType = "center_green";
        this.centerTiles.push({x:position.x,y:item.noise,z:position.y,mesh:mesh});
        mesh.finalColor = color;
      }

      mesh.material.color = this.getTileGrayColor(len);//new THREE.Color(0xececec);
      mesh.grayColor = this.getTileGrayColor(len);//new THREE.Color(0xececec);

      this.allTiles.push(mesh);
    });

    this.makeInstancedFloor();

    let total = this.allTiles.length;

    this.lenTiles = [];
    for(let i = 0; i < total; i++){
      this.lenTiles.push({index:i,len:this.allTiles[i].len});

    }
    this.lenTiles.sort((a,b) => (a.len > b.len) ? 1 : ((b.len > a.len) ? -1 : 0))

  }

  resetClearFloor(){
    this.allTiles.forEach((item, i) => {
      //this.scene.remove(item);
    });
    this.allTiles = [];
  }

  getTileColor(len,instrument){

    let gradient = this.gradients[instrument].tiles;
    let color = gradient[0];
    let centerColor = this.gradients[instrument].center;


    if(len < 8*this.hexSize){ color = new THREE.Color(gradient[0]);  }
    else if(len < 10*this.hexSize){ color = new THREE.Color(gradient[1]); }
    else if(len < 12*this.hexSize){ color = new THREE.Color(gradient[2]); }
    else if(len < 14*this.hexSize){color = new THREE.Color(gradient[3]); }
    else { color = new THREE.Color(gradient[4]); }

    if(len < 5*this.hexSize ){
      color = new THREE.Color(centerColor);
    }

    // if(len > 16*this.hexSize){
    //   color = new THREE.Color(0xff0000);
    // }

    return color;
  }

  getTileGrayColor(len){
    let gradient = [0xececec,0xd1d1d1,0xbcbcbc,0xb2b2b2,0x9b9b9b];
    let color = gradient[0];
    let centerColor = 0xececec;


    if(len < 20){ color = new THREE.Color(gradient[0]);  }
    else if(len < 24){ color = new THREE.Color(gradient[1]); }
    else if(len < 27){ color = new THREE.Color(gradient[2]); }
    else if(len < 30){color = new THREE.Color(gradient[3]); }
    else { color = new THREE.Color(gradient[4]); }

    if(len < 5*this.hexSize ){
      color = new THREE.Color(centerColor);
    }

    return color;
  }

  setCenterTileColors(colorVal){
    this.allTiles.forEach((item, i) => {
      if(item.tileType == "center_green"){
        let color = new THREE.Color(colorVal);
        item.material.color = color;
        item.finalColor = colorVal;
        item.material.needsUpdating = true;
      }
    });
  }

  // deprecated?
  setGradientColor(index,colorVal){

    this.allTiles.forEach((item, i) => {
      let color = null;
      let len = item.len;
      if(len < 20 && index == 0){ color = new THREE.Color(colorVal);  }
      else if(len >= 20 && len < 24 && index ==1){ color = new THREE.Color(colorVal); }
      else if(len >= 24 && len < 27 && index ==2){ color = new THREE.Color(colorVal); }
      else if(len >= 27 && len < 30 && index ==3){color = new THREE.Color(colorVal); }
      else if( len >= 30 && index ==4){ color = new THREE.Color(colorVal); }
      else{
        color = null;
      }

      if(len < 5*this.hexSize){
        color = null;
      }


      if(color != null){
        item.material.color = color;
        item.finalColor = colorVal;
      }
    });

  }

  hexGridTileToPosition(tileX,tileY,size){
    return new THREE.Vector2((tileX + (tileY % 2) * .5) * 1.75 * size, tileY * 1.5 * size);
  }

  makeHex(height, position, r = 2){
    r = this.hexSize;
    let geo = new THREE.CylinderGeometry( r,r, 1, 6, 1, false );
    let color = this.yinkaColors.getRandom();//0xececec;//
    let mesh = new THREE.Mesh( geo, new THREE.MeshToonMaterial( {color: new THREE.Color(color), map:null} ));
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = "hex";
    mesh.position.set(position.x,0,position.y);
    mesh.scale.set(1,height,1);
    return mesh;
  }

  resetTilesFinalColors(instrument){
    this.allTiles.forEach((item, i) => {
      let color = this.getTileColor(item.len,instrument);
      item.finalColor = color;
    });
  }

  resetTilesToGrayscale(){

    this.allTiles.forEach((item, i) => {
      let color = this.getTileGrayColor(item.len);
      item.material.color = color;
    });

  }

  displaceWithAnalyser(dataArray,bufferLength){
    if(!this.lastSum){
      this.lastSum = 0;
    }

    let count = 0;
    let maxD = 10;
    this.allTiles.forEach((item,i) => {
      if(item.tileType == "default" || item.tileType == "pattern"){
        let mesh = item;
        let num = count % bufferLength;
        if(mesh.len > maxD){
          let v = dataArray[num] * .01;
          v *= v;//Math.pow(v,1.5);
          // v *= Math.pow(1 - ((maxD - mesh.len) / maxD), 2);
          v = .25 * mesh.onoise + .75 * v;
          gsap.to( item.scale, { y: v, duration: .5} );
          count++;
        }
      }
    });


  }
};
